import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { callMonitorAPI } from '../../../api/client';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

const DAY_OPTIONS = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
  { value: '*',   label: 'Every day' },
];

const Spinner = () => (
  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
);

export default function CallMonitorSection() {
  const [form, setForm] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callMonitorAPI.getSettings();
      setSettings(res.data);
      setForm(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load call monitor settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        enabled: !!form.enabled,
        target_phone_number: form.target_phone_number || null,
        target_name: form.target_name || 'Customer Hotline',
        vapi_assistant_id: form.vapi_assistant_id || null,
        scenario: form.scenario || null,
        schedule_day_of_week: form.schedule_day_of_week || 'mon',
        schedule_hour: Number(form.schedule_hour ?? 9),
        schedule_minute: Number(form.schedule_minute ?? 0),
        auto_analyse: !!form.auto_analyse,
      };
      const res = await callMonitorAPI.updateSettings(data);
      setSettings(res.data);
      setForm(res.data);
      toast.success('Call monitor settings saved — schedule updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Credentials status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className={`p-3 rounded-lg border ${settings.vapi_configured ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <div className="flex items-center gap-2 text-sm font-medium">
            {settings.vapi_configured
              ? <><CheckCircleIcon className="w-4 h-4 text-green-600" /> <span className="text-green-700">Vapi API connected</span></>
              : <><ExclamationCircleIcon className="w-4 h-4 text-red-600" /> <span className="text-red-700">VAPI_PRIVATE_KEY missing</span></>}
          </div>
          <p className="text-xs text-gray-500 mt-1">Set in backend <code>.env</code> file.</p>
        </div>
        <div className={`p-3 rounded-lg border ${settings.twilio_configured ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
          <div className="flex items-center gap-2 text-sm font-medium">
            {settings.twilio_configured
              ? <><CheckCircleIcon className="w-4 h-4 text-green-600" /> <span className="text-green-700">Twilio configured</span></>
              : <><ExclamationCircleIcon className="w-4 h-4 text-red-600" /> <span className="text-red-700">Twilio credentials missing</span></>}
          </div>
          <p className="text-xs text-gray-500 mt-1">TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, and outbound number in <code>.env</code>.</p>
        </div>
      </div>

      <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
        <input
          type="checkbox"
          id="cm-enabled"
          checked={!!form.enabled}
          onChange={e => setForm({ ...form, enabled: e.target.checked })}
          className="w-4 h-4 accent-primary-600"
        />
        <label htmlFor="cm-enabled" className="text-sm font-medium text-gray-800 select-none">
          Enable scheduled call monitoring
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Target Phone Number (Hotline)</label>
          <input
            type="tel"
            value={form.target_phone_number || ''}
            onChange={e => setForm({ ...form, target_phone_number: e.target.value })}
            placeholder="+1234567890"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Display Name</label>
          <input
            type="text"
            value={form.target_name || ''}
            onChange={e => setForm({ ...form, target_name: e.target.value })}
            placeholder="Customer Hotline"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Vapi Assistant ID</label>
        <input
          type="text"
          value={form.vapi_assistant_id || ''}
          onChange={e => setForm({ ...form, vapi_assistant_id: e.target.value })}
          placeholder="asst_..."
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
        <p className="text-xs text-gray-400 mt-1">Create your mystery-customer assistant in the Vapi dashboard and paste the ID here.</p>
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Mystery-Customer Scenario (optional)</label>
        <textarea
          value={form.scenario || ''}
          onChange={e => setForm({ ...form, scenario: e.target.value })}
          placeholder="e.g. Call as a frustrated customer whose order arrived damaged. Ask about return policy."
          rows={3}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
        />
        <p className="text-xs text-gray-400 mt-1">Overrides the assistant's default system prompt for this call.</p>
      </div>

      <div>
        <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 block">Schedule</label>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={form.schedule_day_of_week || 'mon'}
            onChange={e => setForm({ ...form, schedule_day_of_week: e.target.value })}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            {DAY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
          <span className="text-sm text-gray-500">at</span>
          <input
            type="number" min="0" max="23"
            value={form.schedule_hour ?? 9}
            onChange={e => setForm({ ...form, schedule_hour: e.target.value })}
            className="w-16 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <span className="text-sm text-gray-500">:</span>
          <input
            type="number" min="0" max="59"
            value={form.schedule_minute ?? 0}
            onChange={e => setForm({ ...form, schedule_minute: e.target.value })}
            className="w-16 border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
          <span className="text-xs text-gray-400 ml-2">server timezone</span>
        </div>
      </div>

      <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
        <input
          type="checkbox"
          id="cm-autoanalyse"
          checked={!!form.auto_analyse}
          onChange={e => setForm({ ...form, auto_analyse: e.target.checked })}
          className="w-4 h-4 accent-primary-600"
        />
        <label htmlFor="cm-autoanalyse" className="text-sm text-gray-700 select-none">
          Automatically run AI quality analysis when each call ends
        </label>
      </div>

      <div className="flex justify-end pt-2 border-t border-gray-100">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-40 inline-flex items-center gap-2"
        >
          {saving ? <Spinner /> : 'Save settings'}
        </button>
      </div>
    </form>
  );
}
